// src/App.tsx
import React, { lazy, ReactElement, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { ThemeProvider } from './theme/ThemeProvider';
import { AuthProvider } from './context/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouteConfig } from './model/route';
import { routesConfig } from './config/menuConfig';
import AuthRedirect from './components/auth/AuthRedirect';
import webSocketService from './services/WebSocketService';

// Lazy load components
const Login = lazy(() => import('./components/auth/Login'));
const Callback = lazy(() => import('./components/auth/Callback'));
const ProtectedRoute = lazy(() => import('./components/auth/ProtectedRoute'));
const AdminRoute = lazy(() => import('./components/auth/AdminRoute'));
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const Footer = lazy(() => import('./components/Footer'));
const Header = lazy(() => import('./components/Header'));
const ParallaxBackground = lazy(() => import('./components/ParallaxBackground'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop'));
const UserClaimsPage = lazy(() => import('./pages/UserClaimsPage'));

/**
 * Helper function to render a single route with proper protection
 * @param route The route configuration to render
 * @returns React element for the route
 */
const renderSingleRoute = (route: RouteConfig): ReactElement => {
  if (route.isSecure === true) {
    // Check if this is an admin route
    if (route.roles.includes('admin')) {
      return (
        <Route
          key={route.path}
          element={
            <Suspense fallback={<LoadingFallback />}>
              <AdminRoute />
            </Suspense>
          }
        >
          <Route
            path={route.path}
            element={<Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>}
          />
        </Route>
      );
    }
    // Regular protected route
    return (
      <Route
        key={route.path}
        element={
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute />
          </Suspense>
        }
      >
        <Route
          path={route.path}
          element={<Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>}
        />
      </Route>
    );
  }
  return (
    <Route
      key={route.path}
      path={route.path}
      element={<Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>}
    />
  );
};

/**
 * Helper function to render routes with list/detail/edit pattern
 * @param route The route configuration with list/detail/edit pattern
 * @returns React elements for the routes
 */
const renderListDetailEditRoutes = (route: RouteConfig): ReactElement | null => {
  if (!route.wrapperListDetailEdit || !route.detailElement || !route.editElement) {
    return null;
  }

  const routes = (
    <>
      <Route
        path={route.path}
        element={<Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>}
      />
      <Route
        path={`${route.path}/view/:id`}
        element={<Suspense fallback={<LoadingFallback />}>{route.detailElement}</Suspense>}
      />
      <Route
        path={`${route.path}/new`}
        element={<Suspense fallback={<LoadingFallback />}>{route.editElement}</Suspense>}
      />
      <Route
        path={`${route.path}/edit/:id`}
        element={<Suspense fallback={<LoadingFallback />}>{route.editElement}</Suspense>}
      />
    </>
  );

  return (
    <React.Fragment key={route.path}>
      {route.isSecure ? (
        <Route
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedRoute />
            </Suspense>
          }
        >
          {routes}
        </Route>
      ) : (
        routes
      )}
    </React.Fragment>
  );
};

/**
 * Render routes recursively
 * @param routes Array of route configurations
 * @returns React elements for the routes
 */
const renderRoutes = (routes: RouteConfig[] = routesConfig): ReactElement[] => {
  return routes.map(route => {
    if (route.children) {
      return (
        <React.Fragment key={route.path}>
          {renderSingleRoute(route)}
          {renderRoutes(route.children)}
        </React.Fragment>
      );
    }

    if (route.wrapperListDetailEdit && route.detailElement && route.editElement) {
      const renderedRoute = renderListDetailEditRoutes(route);
      return renderedRoute || <React.Fragment key={route.path} />;
    }

    return renderSingleRoute(route);
  });
};

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress size={100} />
  </Box>
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  // Initialize WebSocket service when the app starts
  useEffect(() => {
    console.log('Initializing WebSocket service...');
    webSocketService.init();

    // Cleanup on unmount
    return () => {
      console.log('Disconnecting WebSocket service...');
      webSocketService.disconnect();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <AuthRedirect>
              <ParallaxBackground>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                  }}
                >
                  <Suspense fallback={<LoadingFallback />}>
                    <Header />
                    <Box
                      component="main"
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 3,
                        pt: 10,
                      }}
                    >
                      <Routes>
                        {/* Public routes */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/app" element={<Callback />} />
                        <Route path="/user-claims" element={<UserClaimsPage />} />

                        {renderRoutes()}
                        {/* Fallback route */}
                        <Route path="*" element={<ErrorPage />} />
                      </Routes>
                    </Box>
                    <Footer />
                  </Suspense>
                </Box>
              </ParallaxBackground>
            </AuthRedirect>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
